import { useState, useEffect } from 'react';
import { aboutApi } from '../../api/about';
import ProgressBar from '../../components/common/ProgressBar';
import { Box } from '@mui/material';
import PageLayout from '../../components/layout/PageLayout';
import SEO from '../../components/common/SEO';
import { Helmet } from 'react-helmet-async';
import { SITE_URL } from '../../utils/env';
import { useUserStore } from '../../stores/userStore';
import ReactQuillEditor from '../../components/Editor/ReactQuillEditor';
import ContentViewer from '../../components/common/ContentViewer';
import CancelConfirmDialog from '../../components/dialog/CancelConfirmDialog';
import BasicButton from '../../components/button/BasicButton';
import EditButton from '../../components/button/EditButton';

const About = () => {
  const isAdmin = useUserStore((state) => state.isAdmin);
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);

  const onClickEdit = () => {
    setEditContent(content);
    setIsEditing(true);
  };

  const handleOnSubmit = async () => {
    try {
      const { data } = await aboutApi.updateContent(editContent);
      setContent(data.content);
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating content:', error);
    }
  };

  const handleOnClickCancel = () => {
    setIsCancelDialogOpen(true);
  };

  const handleOnConfirmCancel = () => {
    setIsCancelDialogOpen(false);
    setIsEditing(false);
  };

  const fetchContent = async () => {
    try {
      const { data } = await aboutApi.getContent();
      setContent(data.content);
    } catch (error) {
      console.error('Error fetching content:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContent();
  }, []);

  if (isEditing) {
    return (
      <PageLayout>
        <ProgressBar isLoading={loading}>
          <Box>
            <ReactQuillEditor value={editContent} onChange={(value) => setEditContent(value)} />
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              gap: 1,
              mt: { xs: '30px', lg: '50px' },
              width: { xs: '100%', md: 'auto' },
            }}
          >
            <BasicButton
              fullWidth
              variant="outlined"
              onClick={handleOnClickCancel}
              sx={{ width: { md: '110px' }, height: '48px' }}
            >
              취소
            </BasicButton>
            <BasicButton
              fullWidth
              variant="contained"
              color="primary"
              onClick={handleOnSubmit}
              sx={{ width: { md: '110px' }, height: '48px' }}
            >
              등록
            </BasicButton>
          </Box>
        </ProgressBar>
        <CancelConfirmDialog
          open={isCancelDialogOpen}
          onClose={() => setIsCancelDialogOpen(false)}
          onConfirm={handleOnConfirmCancel}
        />
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <SEO
        title="About"
        description={content ? content.replace(/<[^>]*>?/gm, '').substring(0, 160) : undefined}
        url={`${SITE_URL}/about`}
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Postpop', item: SITE_URL },
              { '@type': 'ListItem', position: 2, name: 'About' },
            ],
          })}
        </script>
      </Helmet>
      <ProgressBar isLoading={loading}>
        <>
          {isAdmin && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <EditButton onToggle={onClickEdit} />
            </Box>
          )}
          <Box sx={{ minWidth: '320px' }}>
            <ContentViewer text={content} />
          </Box>
        </>
      </ProgressBar>
    </PageLayout>
  );
};

export default About;
